/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  Document, Font, Image, Link, Page, StyleSheet,
  Text, View,
} from '@react-pdf/renderer';
import React from 'react';

import { formatPhoneDotsIntl, formatPhoneNumber, httpsRegex } from 'utils/lib';
import { montserratFont, vidalokaFont } from 'utils/pdf-fonts';

Font.register(vidalokaFont);

Font.register(montserratFont);

const styles = StyleSheet.create({
  page: {
    position: 'relative',
  },
  background: {
    position: 'absolute',
    top: -1, // -1 on all of these to fix off-by-1 errors with the background images
    left: -1,
    right: -1,
    bottom: -1,
  },
  agentInfoContainer: {
    position: 'absolute',
    top: '5.85in',
    left: '0.8in',
    right: 0,
  },
  agentPicture: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '1.1in',
    height: '1.35in',
    objectFit: 'cover',
    borderRadius: '12px',
  },
  teamOrCompanyLogo: {
    position: 'absolute',
    objectFit: 'contain',
    objectPosition: 'center bottom',
  },
  agentInfoText: {
    color: '#1d1d0c',
    position: 'absolute',
    top: '-0.02in',
    left: '1.43in',
  },
  link: {
    color: '#1d1d0c',
    textDecoration: 'none',
  },
  activityGuideWatermark: {
    position: 'absolute',
    bottom: '0.4in',
    right: '0.8in',
    fontFamily: 'Montserrat',
    fontWeight: 700,
    fontSize: '8px',
  },
  bodyText: {
    position: 'absolute',
    fontFamily: 'Montserrat',
    fontSize: '14px',
    color: '#2a2a2a',
    textAlign: 'justify',
    lineHeight: '18px',
    letterSpacing: '1px',
  },
  bullet: {
    marginRight: 6,
  },
  bulletItem: {
    flexDirection: 'row',
    marginBottom: 4,
  },
});

const listStyles = StyleSheet.create({
  line1: {
    flexDirection: 'row',
  },
  bold: {
    fontWeight: 'bold',
  },
  description: {
    fontWeight: 'normal',
  },
  subline: {
    marginLeft: 10, // extra indent for sub-list style
    fontStyle: 'italic',
    marginTop: 2,
  },
});

const ActivityGuideWatermark = ({ light }: { light: boolean }) => (
  <Text style={{
    ...styles.activityGuideWatermark,
    color: light ? '#f1f0ea' : '#2a2a2a',
  }}
  >
    [YOUR CITY] | ACTIVITY GUIDE
  </Text>
);

const AgentInfo = ({ userInfo }: any) => (
  <View style={styles.agentInfoContainer}>
    <Image src={userInfo.agentInfo.agentPictureUrl} style={styles.agentPicture} />

    <View style={styles.agentInfoText}>
      <Text style={{
        fontFamily: 'Vidaloka',
        fontSize: '22px',
        textTransform: 'uppercase',
        marginBottom: '3px',
      }}
      >
        {`${userInfo.firstName} ${userInfo.lastName ?? ''}`}
      </Text>

      <Text style={{
        fontFamily: 'Montserrat',
        fontWeight: 700,
        fontSize: '9px',
        textTransform: 'uppercase',
      }}
      >
        {`Lic. # ${userInfo.agentInfo.agentDreNumber}`}
      </Text>

      <Text style={{
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: '17px',
        margin: '6px 0',
        letterSpacing: '1.2px',
      }}
      >
        <Link src={`tel:${formatPhoneNumber(userInfo.agentInfo.marketingPhoneNumber, true)}`} style={styles.link}>
          {formatPhoneDotsIntl(userInfo.agentInfo.marketingPhoneNumber)}
        </Link>
      </Text>

      <Text style={{
        fontFamily: 'Montserrat',
        fontWeight: 400,
        fontSize: '11px',
      }}
      >
        <Link src={`mailto:${userInfo.agentInfo.marketingEmail}`} style={styles.link}>
          {userInfo.agentInfo.marketingEmail}
        </Link>
      </Text>

      {userInfo.agentInfo.website && (
        <Text style={{
          fontFamily: 'Montserrat',
          fontWeight: 400,
          fontSize: '11px',
        }}
        >
          <Link src={userInfo.agentInfo.website} style={styles.link}>
            {userInfo.agentInfo.website.replace(httpsRegex, '')}
          </Link>
        </Text>
      )}
    </View>
  </View>
);

const ImageCard = ({ pictureUrl, title, subtitle }: { pictureUrl: string; title: string; subtitle: string }) => (
  <View style={{ width: '2.25in', color: '#f1f0ea', textAlign: 'center' }}>
    <Image
      source={pictureUrl}
      style={{
        width: '100%', height: '2.25in', objectFit: 'cover', borderRadius: '12px',
      }}
    />
    <Text style={{ fontFamily: 'Vidaloka', fontSize: '23px', margin: '12px 0 6px' }}>{title}</Text>
    <Text style={{ fontFamily: 'Montserrat', fontSize: '14px' }}>{subtitle}</Text>
  </View>
);

const UltimateCityGuideDocument = ({ name, userInfo }: any) => {
  const teamOrCompanyLogoUrl = userInfo.agentInfo.teamLogoLightUrl
        ?? userInfo.agentInfo.teamLogoUrl
        ?? userInfo.agentInfo.companyLogoLightUrl
        ?? userInfo.agentInfo.companyLogoUrl;

  return (
    <Document title={name} author="Harvist">
      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/1.png" style={styles.background} />

        <View style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '0.7in',
          right: 0,
          color: '#f1f0ea',
        }}
        >
          <Text style={{
            position: 'absolute',
            top: '1.5in',
            left: 0,
            fontFamily: 'Montserrat',
            fontWeight: 700,
            fontSize: '17px',
          }}
          >
            Welcome to [YOUR CITY], USA!
          </Text>

          <Text style={{
            position: 'absolute',
            top: '2.6in',
            left: 0,
            fontFamily: 'Vidaloka',
            fontSize: '57px',
          }}
          >
            Ultimate
            {'\n'}
            [Your City]
            {'\n'}
            Activity Guide
          </Text>

          <Text style={{
            position: 'absolute',
            top: '6.75in',
            left: 0,
            fontFamily: 'Montserrat',
            fontWeight: 700,
            fontSize: '14px',
          }}
          >
            {`Presented by ${userInfo.firstName} ${userInfo.lastName ?? ''}`}
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/2.png" style={styles.background} />
        <ActivityGuideWatermark light />

        <Text style={{
          ...styles.bodyText,
          top: '2.7in',
          left: '0.8in',
          width: '4.55in',
        }}
        >
          Whether you’re just passing through, staying for an extended vacation, or scouting out your future home we’re certain you’re going to love [YOUR CITY].
          {'\n\n'}
          [YOUR CITY] is known for its small-town charm, with big city fun. In this guide we’re dishing the best places to shop, eat, relax, explore, and more, so that you can live like a local!
          {'\n\n'}
          See you out there!
          {'\n\n'}
          <Text style={{ fontSize: '12.5px', fontStyle: 'italic' }}>
            Be sure to check the businesses’ websites for hours, prices, and additional details.
          </Text>
        </Text>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/3.png" style={styles.background} />
        <ActivityGuideWatermark light={false} />

        <View style={{
          ...styles.bodyText,
          top: '2.9in',
          left: '4.6in',
          width: '5.5in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Mami’s Closet: '}
              <Text style={listStyles.description}>
                A carefully curated collection of clothing from local designers.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
            {'\n\n'}
          </Text>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• PV Treasures: '}
              <Text style={listStyles.description}>
                This is our go-to spot for gifts for every occasion and unique home décor items.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
            {'\n\n'}
          </Text>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Galleria C: '}
              <Text style={listStyles.description}>
                The biggest collection of local vendors under one roof! From handcrafted soap to leather bags, you’re sure to find something for everyone on your list. Don’t forget to grab a jar of G-Bee’s honey… it’s perfect with a cup of PV’s Herbal Tea after a long day!
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/4.png" style={styles.background} />
        <ActivityGuideWatermark light />

        <View style={{
          position: 'absolute', top: '3.0in', left: '1.3in', right: '1.3in', flexDirection: 'row', justifyContent: 'space-between',
        }}
        >
          <ImageCard pictureUrl="/free-resources/ultimate-city-activity-guide/card-image-1.png" title="Pleasantville Common" subtitle="123 Main Street" />
          <ImageCard pictureUrl="/free-resources/ultimate-city-activity-guide/card-image-2.png" title="Mayfield Mall" subtitle="123 Main Street" />
          <ImageCard pictureUrl="/free-resources/ultimate-city-activity-guide/card-image-3.png" title="Pembroke Mills" subtitle="123 Main Street" />
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/5.png" style={styles.background} />
        <ActivityGuideWatermark light={false} />

        <Text style={{
          ...styles.bodyText,
          top: '2.25in',
          left: '4.2in',
          width: '5.2in',
        }}
        >
          Did we mention that [YOUR CITY] is home to some delicious local eateries?  No matter what time of day, you’re sure to find something that hits the spot.
        </Text>

        <View style={{
          ...styles.bodyText,
          top: '4.5in',
          left: '4.3in',
          width: '5.75in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Stella’s: '}
              <Text style={listStyles.description}>
                If you’re looking for a fresh, healthy start to your day, stop at Stella’s. We highly recommend the Spinach Crepe and Sunrise Smoothie.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
            {'\n\n'}
          </Text>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Wafflelot: '}
              <Text style={listStyles.description}>
                Love waffles, French toast, and pancakes? You must hit up Wafflelot. Their house made syrup and jams taste good on everything… seriously.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/6.png" style={styles.background} />
        <ActivityGuideWatermark light />

        <View style={{
          ...styles.bodyText,
          top: '3.5in',
          left: '0.9in',
          width: '4.5in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Trailblazers: '}
              <Text style={listStyles.description}>
                For the best barbecue in town visit Trailblazers. Be sure to get there early so that you can grab the Sliced Beef Brisket with a side of the Smokehouse Chili.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
            {'\n\n'}
          </Text>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Chopped Green: '}
              <Text style={listStyles.description}>
                Looking for a light lunch? Enjoy a delicious soup, salad, or sandwich on the patio at Chopped Green.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/7.png" style={styles.background} />
        <ActivityGuideWatermark light={false} />

        <View style={{
          ...styles.bodyText,
          top: '3.4in',
          left: '5.45in',
          width: '4.05in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Hart’s Kitchen & Bar: '}
              <Text style={listStyles.description}>
                Contemporary tavern offerings & classic eats with a modern twist. Family friendly and always tasty.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
            {'\n\n'}
          </Text>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Evario Wine & Tapas: '}
              <Text style={listStyles.description}>
                Pleasantville’s favorite chic wine bar featuring a variety of Spanish & European tapas in an intimate, red-walled space.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/8.png" style={styles.background} />
        <ActivityGuideWatermark light />

        <View style={{
          ...styles.bodyText,
          top: '3.5in',
          left: '1.0in',
          width: '4.5in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Buzz Coffee: '}
              <Text style={listStyles.description}>
                Looking for a little pick me up while touring Pleasantville? Be sure to stop at one of our favorite coffee spots – Buzz!
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
            {'\n\n'}
          </Text>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Wave Coffee Bar: '}
              <Text style={listStyles.description}>
                Wave is the perfect place to meet with friends, family, and clients. Be sure to check their upcoming events; they host concerts with local artists every Saturday night.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/9.png" style={styles.background} />
        <ActivityGuideWatermark light={false} />

        <View style={{
          ...styles.bodyText,
          top: '3.6in',
          left: '5.5in',
          width: '4.3in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• BonBon Bakery: '}
              <Text style={listStyles.description}>
                You simply cannot visit Pleasantville without stopping at BonBon for a dozen of their one-of-a-kind donuts.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
            {'\n\n'}
          </Text>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Paradiso Cakes: '}
              <Text style={listStyles.description}>
                Paradiso is your Pleasantville go-to for cakes for all occasions. From wedding cakes, to birthday cakes, to cupcakes ‘just because’ we’ve never tasted anything we didn’t LOVE at Paradiso’s.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • Located at 123 Main Street
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/10.png" style={styles.background} />
        <ActivityGuideWatermark light />

        <Text style={{
          ...styles.bodyText,
          top: '1.8in',
          left: '0.8in',
          width: '5.2in',
          fontSize: '15px',
        }}
        >
          [YOUR CITY] knows how to celebrate! If you happen to be visiting us during one of our famous annual festivals, be sure to stop by. All are welcome and we’d love to meet you!
        </Text>

        <View style={{
          ...styles.bodyText,
          top: '3.3in',
          left: '0.8in',
          width: '5.2in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• [YOUR CITY] Days: '}
              <Text style={listStyles.description}>
                Each Spring we welcome warmer temperatures with [YOUR CITY]. This free, family friendly event includes a business expo, craft fair, carnival, food vendors, and live local entertainment.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • For more information visit www.website.com
            {'\n\n'}
          </Text>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Fourth of July: '}
              <Text style={listStyles.description}>
                [YOUR CITY] is known county-wide for our Independence Day celebrations. Join us in the community park for live entertainment, food trucks, and of course, some of the best fireworks in the area.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • For more information visit www.website.com
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/11.png" style={styles.background} />
        <ActivityGuideWatermark light={false} />

        <View style={{
          ...styles.bodyText,
          top: '2.05in',
          left: '4.8in',
          width: '5.05in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Autumnfest: '}
              <Text style={listStyles.description}>
                Festival-goers love Autumnfest. Get a jump start on your holiday shopping with our favorite local vendors. Kids love the carnival, foodies love the food trucks from across the state, and music lovers love the live entertainment. There’s something for everyone in your crew.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • For more information visit www.website.com
            {'\n\n'}
          </Text>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Festival of Trees: '}
              <Text style={listStyles.description}>
                This is a holiday favorite in [YOUR CITY]. Spend the evening wandering taking in the beauty of themed trees created by local businesses, schools, and community groups. All proceeds for this event go towards a local charity of choice. Be sure to grab a hot chocolate at the chalet and bundle up.
              </Text>
            </Text>
          </View>
          <Text style={listStyles.subline}>
            • For more information visit www.website.com
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/12.png" style={styles.background} />
        <ActivityGuideWatermark light={false} />

        <Text style={{
          ...styles.bodyText,
          top: '1.7in',
          left: '5.38in',
          width: '4.5in',
          fontSize: '15px',
        }}
        >
          If you’re like me, you love getting out to enjoy the outdoors. The hiking in [YOUR CITY] is second to none. Whether you’re looking for something easy, moderate, or strenuous, we’ve got you covered. Be sure to take lots of water and plan your route in advance. For more information on trail conditions visit www.website.com.
        </Text>

        <View style={{
          ...styles.bodyText,
          top: '4.3in',
          left: '5.58in',
          width: '4.18in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>• [YOUR CITY] River Valley Park:</Text>
          </View>
          <Text style={{ ...listStyles.subline, fontStyle: 'normal' }}>
            <Text style={listStyles.bold}>
              {'• Difficulty: '}
              <Text style={listStyles.description}>
                Easy (Paved Trail)
              </Text>
            </Text>
          </Text>
          <Text style={{ ...listStyles.subline, fontStyle: 'normal' }}>
            <Text style={listStyles.bold}>
              {'• Length: '}
              <Text style={listStyles.description}>
                Over 20 miles of walking trails. You can walk as much or as little as you’d like!
              </Text>
            </Text>
          </Text>
          <Text style={{ ...listStyles.subline, fontStyle: 'normal' }}>
            <Text style={listStyles.bold}>• </Text>
            Parking is free at the Pleasantville Community Park. Enjoy great views of the river valley. Trails are wheelchair and stroller accessible. Pack a lunch and enjoy the picnic tables along the way.
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/13.png" style={styles.background} />
        <ActivityGuideWatermark light />

        <View style={{
          ...styles.bodyText,
          top: '2.15in',
          left: '0.9in',
          width: '4.25in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>• Red Fox Trail:</Text>
          </View>
          <Text style={{ ...listStyles.subline, fontStyle: 'normal' }}>
            <Text style={listStyles.bold}>
              {'• Difficulty: '}
              <Text style={listStyles.description}>
                Moderate
              </Text>
            </Text>
          </Text>
          <Text style={{ ...listStyles.subline, fontStyle: 'normal' }}>
            <Text style={listStyles.bold}>
              {'• Length: '}
              <Text style={listStyles.description}>
                2 miles one way
              </Text>
            </Text>
          </Text>
          <Text style={{ ...listStyles.subline, fontStyle: 'normal' }}>
            <Text style={listStyles.bold}>• </Text>
            Red Fox Trail is one of our favorite local hikes. The trailhead is located just north of the city at 12345 67th Street. Be sure to keep an eye out for deer!
            {'\n\n\n'}
          </Text>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>• Pleasant Mountain Trail:</Text>
          </View>
          <Text style={{ ...listStyles.subline, fontStyle: 'normal' }}>
            <Text style={listStyles.bold}>
              {'• Difficulty: '}
              <Text style={listStyles.description}>
                Strenuous
              </Text>
            </Text>
          </Text>
          <Text style={{ ...listStyles.subline, fontStyle: 'normal' }}>
            <Text style={listStyles.bold}>
              {'• Length: '}
              <Text style={listStyles.description}>
                5 miles one way
              </Text>
            </Text>
          </Text>
          <Text style={{ ...listStyles.subline, fontStyle: 'normal' }}>
            <Text style={listStyles.bold}>• </Text>
            This is tough hike, with steep elevation gains, but if you’re up for a challenge, it is so worth it. At the top of the hike you’ll enjoy panoramic views of Pleasantville and the surrounding areas!
          </Text>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/14.png" style={styles.background} />
        <ActivityGuideWatermark light={false} />

        <Text style={{
          ...styles.bodyText,
          top: '1.6in',
          left: '4.7in',
          width: '5.1in',
        }}
        >
          If you’re looking for somewhere to relax the afternoon away while the
          kids burn off some energy, you are going to love the [YOUR CITY] Park System. There are so many parks throughout our city, but these are our top three favorites:
        </Text>

        <View style={{
          ...styles.bodyText,
          top: '3.6in',
          left: '4.8in',
          width: '5.0in',
        }}
        >
          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Opportunity Park: '}
              <Text style={listStyles.description}>
                This park includes trails for walking and biking, covered picnic areas, a playground. Located at 123 Main Street.
                {'\n\n'}
              </Text>
            </Text>
          </View>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• Navigation Park: '}
              <Text style={listStyles.description}>
                If it’s a hot summer day, this is the place to be! Enjoy the splash pad and keep an eye out for the ice-cream truck. Located at 123 Main Street.
                {'\n\n'}
              </Text>
            </Text>
          </View>

          <View style={listStyles.line1}>
            <Text style={listStyles.bold}>
              {'• City Center Park: '}
              <Text style={listStyles.description}>
                This park is right in the heart of the city. It’s a favorite spot for a weekday lunch and on the weekend it is home to our very own Farmer’s Market. Located at 123 Main Street.
              </Text>
            </Text>
          </View>
        </View>
      </Page>

      <Page size={{ width: '10.7in', height: '8in' }} style={styles.page}>
        <Image src="/free-resources/ultimate-city-activity-guide/15.png" style={styles.background} />

        <Text style={{
          ...styles.bodyText,
          top: '1.65in',
          left: '0.8in',
          width: '4.5in',
        }}
        >
          I absolutely love living, working, and playing in [YOUR CITY]. We moved our family to [YOUR CITY] 15 years ago and never looked back. From the ample amenities to the strong sense of community, there is nowhere else we’d rather call home. I hope you are able to enjoy some of these local hotspots and that you love our area as much as I do!
          {'\n\n'}
          If you are considering a move to [YOUR CITY], I would be honored to be a part of your journey.
        </Text>

        <AgentInfo userInfo={userInfo} />

        <Image
          src={teamOrCompanyLogoUrl}
          style={{
            ...styles.teamOrCompanyLogo,
            top: '6.0in',
            left: '7.85in',
            width: '1.5in',
            height: '1.3in',
          }}
        />
      </Page>
    </Document>
  );
};

export default UltimateCityGuideDocument;
